/* eslint-disable no-console */
/* eslint-disable class-methods-use-this */
/*
 * This code is the intellectual property of Bulb Inc. and other software publishers;
 * it may not be altered, copied or disclosed without prior written approval.
 * Copyright 2020, Bulb Inc. For more information, please email contact@hellobulb.com.
*/

import { HttpRequest } from '@angular/common/http';
import { Inject, Injectable } from '@angular/core';

import {
  init, captureException, withScope, captureMessage, SeverityLevel, Scope, getCurrentScope,
} from '@sentry/angular';
import { AppConfigService } from './services/app-config/app-config.service';

const Severity = {
  Error: 'error' as SeverityLevel,
};

const rejectedErrorMessages = new RegExp([
  'Cannot read property \'statusText\' of null',
].join('|'), 'i');

/**
 * Constant for finding object Id from a url.
 */
const objectIdRegex: RegExp = /[0-9a-zA-Z]{24}/;

/**
 * Angular service that functions as central location to handle errors and exceptions.
 */
@Injectable()
export class ErrorService {
  /**
   * Default Constructor.
   */
  constructor(
    @Inject('BulbAuthService') private BulbAuthService: any,
    private AppConfigService: AppConfigService,
  ) {
    /**
     * Initialize the Sentry service to recognize the correct project and environment information
     */
    init({
      attachStacktrace: true,
      dsn: this.AppConfigService.sentry.client.dsn,
      environment: this.AppConfigService.sentry.client.environment,
      release: this.AppConfigService.sentry.client.release,
      ignoreErrors: [
        // Don't report failed attempts to login - it is usually (Not Found) but coming from my.bulbapp.com it's ()
        /POST \/b\/rest\/auth\/authenticate 404 \(.*\)/,

        // Don't report missing resumes
        /GET \/b\/rest\/resumes\/([^ ])+ 404 \(Not Found\)/,

        // Don't report missing cover image
        /DELETE \/b\/rest\/units\/([^ ])+\/coverimage 404 \(.*\)/,

        // Don't report missing embed block
        /POST \/b\/rest\/units\/([^ ])+\/embedblock 404 \(.*\)/,

        // Don't report deleted group-code
        /POST \/b\/rest\/token\/member\/([^ ])+ 404 \(.*\)/,

        // Don't report non existing group-code
        /GET \/b\/rest\/token\/group\/([^ ])+ 404 \(.*\)/,

        // Don't report errors starting with this label.
        /Non-Error promise rejection captured/,

        // Don't report angular digest cycle errors.
        /\[\$rootScope:inprog] ([^ ])+ already in progress/,

        // These two errors are specific to cke page break. Ignore these errors for this time(BULBAPP-8794).
        /Cannot read properties of null \(reading 'getTrimmed'\)/,
        /Cannot read properties of null \(reading 'parent'\)/,

        // These two below are benign error which simply indicates not all animations observations could be sent
        // in a single frame and can be ignored. BULBAPP-8397 && BULBAPP-9552
        /ResizeObserver loop limit exceeded/,
        /ResizeObserver loop completed with undelivered notifications/,
      ],
      sampleRate: this.AppConfigService.sentry.client.sampleRate,
      beforeSend: (event) => (rejectedErrorMessages.test(event.message) ? undefined : event),
    });
    getCurrentScope().setUser(this.configureSentryScope());
  }

  /**
   * Method to handle capturing the different kinds of errors.
   *
   * Different error types are handled:  Calls to restangular, calls to NgxRestangular, Errors thrown as exceptions,
   * and error strings.  Anything else is still captured, but the error object is passed in as context to the error
   * service.
   *
   * In addition, since we know these are the result of a save, we handle more types of errors than the default
   * error interceptors, as saves should never fail.  So we capture even errors with status 400 and 403.
   *
   * @param error Error which occurs during the save.
   * @param captureBadRequestsAndForbidden Whether 400 and 403 status errors from Restangular and
   * NgxRestangular saves should be captured.  Default is false.
   */
  public captureAnyError(error : any, captureBadRequestsAndForbidden : boolean = false) {
    if (error?.config?.url) {
      // We only want to capture 400 and 403, because the built-in interceptor will capture other failures
      if (captureBadRequestsAndForbidden && (error.status === 400 || error.status === 403)) {
        this.captureRestangularError(error);
      }
    } else if (error instanceof Error) {
      this.handleException(error);
    } else if (typeof error === 'string') {
      this.captureErrorMessage(error, {});
    } else {
      this.captureErrorMessage('Error of unknown type while saving', error);
    }
  }

  /**
   * Perform standard error processing for the provided error message.
   *
   * @param message Error message.
   * @param context optional additional context for the error.
   * @private
   */
  public captureErrorMessage(message: string, context? : any) {
    try {
      withScope((scope) => {
        if (context) {
          scope.setExtra('context', context);
        }
        captureMessage(message, Severity.Error);
      });
    } catch (error) {
      // eslint-disable-next-line no-console
      console.log(`failed to capture message : ${error.message}`);
    }
  }

  /**
   * Perform standard error processing for the provided exception.
   *
   * @exception Exception that has been caught and needs to be handled.
   */
  public handleException(exception : any) : void {
    try {
      captureException(exception);
    } catch (error) {
      console.log(`failed to capture exception : ${error.message}`);
    }
  }

  /**
   * Capture and report an error which occurred during a http request.
   *
   * @param request The request that had an error.
   * @param response The error response.
   * @public
   */
  public captureAngularHttpError(request: HttpRequest<any>, response: any) : void {
    // Note that this method looks very similar to the method for restangular errors, but the structure
    // of the response is different.

    try {
      if (!response.captured) {
        response.captured = true;
        withScope((scope) => {
          this.setFingerprintAndExtras(
            scope,
            request.urlWithParams,
            request.method,
            response.status,
            request.body,
            response.message,
          );
          captureMessage(`${request.method} ${request.urlWithParams} ${
            response.status} (${response.statusText})`, Severity.Error);
        });
      }
    } catch (error) {
      console.log(`failed to capture http angular error : ${error.message}`);
    }
  }

  /**
   * Error Interceptor for restangular calls.
   *
   * @param response Response from the call.
   */
  interceptRestangularError(response: any /* deferred: any, responseHandler: any */) : boolean {

    try {
      if (response.status === 400 || response.status === 403) {
        // We don't consider 400/403 errors by default.  If it is an error, the originator of the request should
        // handle the error rather than relying on the default error handler.

      } else if (response.status >= 400) {
        // NOTE: 404 is considered an error on the client but not on the server because we shouldn't be creating
        // any links or making calls from the client which result in a 404.
        this.captureRestangularError(response);
      }
    } catch (error) {
      console.log(`failed to intercept Restangular error : ${error.message}`);
    }

    // Regardless if we are capturing this error or not, we want to allow the caller a chance to handle the error
    // as they have more context on what is going on.

    // Optimally we would like the caller the first opportunity to handle an error before this interceptor, but
    // that isn't a trivial task, so this interceptor runs always runs first.
    return true;
  }

  /**
   * Capture and report an error which occurred during a restangular request.
   * @param response The error response from restangular.
   * @private
   */
  private captureRestangularError(response: any) : void {
    // Note that this method looks very similar to the method for ngx-restangular errors, but the structure
    // of the response is different.

    try {
      if (!response.captured) {
        response.captured = true;
        withScope((scope) => {
          this.setFingerprintAndExtras(
            scope,
            response.config.url,
            response.config.method,
            response.status,
            response.config.data,
            response.data,
          );
          if (response.status === -1) {
            const errorJson = JSON.stringify(response, Object.getOwnPropertyNames(response));

            // Log the error JSON as extra data to Sentry
            captureException(response, {
              extra: {
                errorJson,
              },
            });
          } else {
            captureMessage(`${response.config.method} ${response.config.url} ${
              response.status} (${response.statusText})`, Severity.Error);
          }
        });
      }
    } catch (error) {
      console.log(`failed to capture Restangular error : ${error.message}`);
    }
  }

  /**
   * Configure the context to send with Sentry exceptions/messages/events.
   * @param scope Scope object provided by Sentry to be configured.
   */
  private configureSentryScope() : any {
    // Set the current user information if a user is logged in.  Clear it out otherwise.
    const user = this.BulbAuthService.currentUser;
    if (user.isAuthenticated()) {
      return {
        id: user.id,
        username: user.username,
      };
    }
    return {};

  }

  /**
   * Method to set fingerprint and extras for sentry.
   *
   * @param scope
   * @param originalUrl Full url to modify.
   * @param method HTTP method name.
   * @param status response status.
   * @param requestBody request body.
   * @param responseBody response body,
   *
   * @return object with fingerprint details and objectId.
   */
  public setFingerprintAndExtras(
    scope: Scope,
    originalUrl: string,
    method: string,
    status: number,
    requestBody: unknown,
    responseBody: unknown,
  ) {
    let id: any;
    let fingerPrint: string;
    let modifiedUrl: string;
    const match = originalUrl.match(objectIdRegex);
    if (match) {
      modifiedUrl = originalUrl.replace(objectIdRegex, '{id}');
      id = match[0];
      fingerPrint = `${method} ${modifiedUrl} ${status}`;
    } else {
      fingerPrint = `${method} ${originalUrl} ${status}`;
    }
    scope.setFingerprint([fingerPrint]);
    if (id) {
      scope.setExtra('urlPathId', id);
    }
    scope.setExtra('requestBody', requestBody);
    scope.setExtra('responseBody', responseBody);
  }
}
